/**
 * 비전 카메라 영상(go2rtc) 설정.
 *
 * ## 무엇을 읽는가
 *
 * 카메라는 RTSP 로 오지만 **브라우저는 RTSP 를 못 받는다.** go2rtc 가 같은 스트림을 자기
 * API 포트로 WebRTC · MSE 로도 내므로 프론트는 그쪽 **WebSocket 한 개**(`/api/ws?src=<이름>`)에
 * 붙는다. 여기서 정하는 것은 그 **기준 경로**와 **스트림 이름**뿐이다.
 *
 * ## 🔴 「없는 것」이 기본이다
 *
 * REST 주소와 달리 영상은 **설정이 없으면 없는 것이 정상**이다 — 그래서 스트림 이름에
 * 기본값을 두지 않는다. 이름이 없으면 화면은 종전대로 「카메라 영상 연결 없음」을 적는다.
 * 주소(`VITE_DEV_VISION_TARGET`)도 소스에 박지 않는다 — dev 프록시 설정에서만 읽는다.
 *
 * ## 🔴 모듈 ↔ 카메라 대응은 계약에 없다 (BR-40)
 *
 * 어느 모듈이 어느 카메라를 보는지 서버가 말해 주지 않는다. 그래서 표를 **설정으로** 열어 두고
 * (`streams.cent` · `.asm` · `.stain`), 개별 지정이 없으면 기본 스트림 하나가 세 모듈에 다 걸린다.
 * ⚠ 이 폴백은 「일단 붙여 본다」(eric 2026-09-21)를 위한 것이지 대응이 확정됐다는 뜻이 아니다.
 *
 * 값의 출처는 둘이고 **런타임이 이긴다** — 배포는 같은 이미지를 쓰고 컨테이너 기동 시
 * `/config.js` 가 `window.__CTCELLS_CONFIG__.vision` 을 채운다(docs/deploy.md).
 */
import { isRecord, positiveNumber, runtimeSection } from './runtimeConfig.ts'

/**
 * dev 서버가 go2rtc 를 프록시하는 경로.
 * ⚠ `packages/build-config/src/vite.ts` 의 프록시 키와 **같은 값이어야 한다**
 *   (REST 경로 `/api/v1/*` 도 같은 식으로 양쪽에 적혀 있다 — build-config 는 core 를 물지 않는다).
 */
export const VISION_PROXY_PATH = '/go2rtc'

/**
 * 모듈 하나의 영상 서버를 따로 넘기는 경로 — `/go2rtc-<모듈 키>`(eric 2026-10-30 「어셈블러 · 원심분리기 아이피」).
 * 영상 서버가 모듈마다 다른 PC 에 서므로 같은 오리진에서 모듈별로 갈라 준다.
 * ⚠ `packages/build-config/src/vite.ts` 의 `visionProxyPathFor` · `docker/nginx.share.conf.template` 의
 *   `location /go2rtc-<키>/` 와 **같은 규칙이어야 한다.**
 * ⚠ `/go2rtc/<키>` 가 아니라 `-` 로 붙인다 — `/go2rtc/` 아래는 go2rtc 자기 경로(`/api/...`)가 그대로 지나간다.
 */
export function visionProxyPathFor(moduleKey: string): string {
  return `${VISION_PROXY_PATH}-${moduleKey}`
}

/** 경로에 들어갈 모듈 키. 형이 틀리면(`../` 류) 버린다 */
const MODULE_KEY_RE = /^[a-z][a-z0-9-]*$/

export interface VisionConfig {
  /**
   * go2rtc API 기준 경로. 비우면 dev 프록시 경로(`/go2rtc`)라 **같은 오리진**으로 나간다.
   * 운영에서 프록시가 없다면 런타임 설정으로 절대 주소를 준다.
   */
  baseUrl: string
  /**
   * 모듈별 기준 경로. **지정이 있는 모듈만** 여기 들고, 없는 모듈은 `baseUrl` 로 간다 —
   * 🔴 한 모듈의 지정이 다른 모듈로 새지 않는다(남의 장비 영상이 된다).
   */
  baseUrls: Readonly<Record<string, string>>
  /** 모듈별 스트림 이름. 지정이 없는 모듈은 `defaultStream` 으로 떨어진다 */
  streams: Readonly<Record<string, string>>
  /** 모듈 지정이 없을 때 쓰는 스트림 이름. 없으면 **영상 없음**이 정답이다 */
  defaultStream: string | undefined
  /** WebRTC 협상을 이 시간 안에 못 붙이면 MSE 로 내려간다(ms) */
  negotiateTimeoutMs: number
  /** 끊긴 뒤 다시 붙기까지 기다리는 시간(ms) */
  reconnectDelayMs: number
}

/**
 * WebRTC 협상 한도. **WebKitGTK(Tauri 리눅스 웹뷰)는 WebRTC 가 꺼진 빌드가 흔하다** —
 * 그때는 객체가 아예 없어서 즉시 MSE 로 가지만, 있는데 안 붙는 경우도 있어 한도를 둔다.
 */
const DEFAULT_NEGOTIATE_TIMEOUT_MS = 5_000
const DEFAULT_RECONNECT_DELAY_MS = 3_000

export interface VisionConfigInput {
  runtime?: Record<string, unknown> | undefined
  env?:
    | {
        baseUrl?: string | undefined
        /** 기본 스트림 이름 */
        stream?: string | undefined
        /** 모듈별 지정. `cent=a,asm=b,stain=c` */
        streams?: string | undefined
        /**
         * 모듈별 기준 경로. `cent=/go2rtc-cent,asm=/go2rtc-asm`.
         * dev · 빌드에서는 vite 설정이 `VITE_DEV_VISION_TARGETS` 의 키로 채운다(손으로 적지 않아도 된다)
         */
        baseUrls?: string | undefined
      }
    | undefined
}

export function resolveVisionConfig(input: VisionConfigInput): VisionConfig {
  const runtime = input.runtime ?? {}
  const env = input.env ?? {}

  return {
    baseUrl: trimSlash(text(runtime['baseUrl']) ?? text(env.baseUrl) ?? VISION_PROXY_PATH),
    baseUrls: baseUrlRecord({
      ...parseStreamMap(env.baseUrls),
      ...streamRecord(runtime['baseUrls']),
    }),
    streams: { ...parseStreamMap(env.streams), ...streamRecord(runtime['streams']) },
    defaultStream: text(runtime['stream']) ?? text(env.stream),
    negotiateTimeoutMs:
      positiveNumber(runtime['negotiateTimeoutMs']) ?? DEFAULT_NEGOTIATE_TIMEOUT_MS,
    reconnectDelayMs: positiveNumber(runtime['reconnectDelayMs']) ?? DEFAULT_RECONNECT_DELAY_MS,
  }
}

let cached: VisionConfig | undefined

export function visionConfig(): VisionConfig {
  cached ??= resolveVisionConfig({
    runtime: runtimeSection('vision'),
    env: {
      baseUrl: import.meta.env.VITE_VISION_BASE_URL,
      stream: import.meta.env.VITE_VISION_STREAM,
      streams: import.meta.env.VITE_VISION_STREAMS,
      baseUrls: import.meta.env.VITE_VISION_BASE_URLS,
    },
  })
  return cached
}

/** 테스트 전용 */
export function resetVisionConfigCache(): void {
  cached = undefined
}

/**
 * 이 모듈이 볼 스트림 이름. **없으면 `undefined` 이고 화면은 플레이어를 세우지 않는다.**
 * 🔴 이름을 지어내지 않는다 — 설정에 없는 카메라를 화면이 발명하면 「남의 장비 영상」이 된다.
 */
export function visionStreamFor(
  moduleKey: string,
  config: VisionConfig = visionConfig(),
): string | undefined {
  return primaryVisionStream(visionStreamOptions(moduleKey, config))
}

/** 한 모듈의 카메라 하나 — 탭 이름(`label`)과 go2rtc 스트림 이름 */
export interface VisionStreamOption {
  label: string
  stream: string
  /** 처음 열 때 이 카메라를 보인다(설정의 `*`) */
  primary?: true
}

/**
 * 이 모듈이 고를 수 있는 카메라들(eric 2026-10-18 「ir 랑.. color 두개지? 이거를 탭별로 선택가능하게 해줘」).
 * 값 하나에 `|` 로 여럿을 적고, 각 조각은 `이름:스트림` 또는 `스트림`(이름 = 스트림)이다 —
 * `cent=RGB:color|IR:ir_left`. 하나뿐이면(종전 표기) 탭 없이 그 하나다. 없으면 빈 목록 — 지어내지 않는다.
 * 탭 차례는 적은 차례 그대로이고, 처음 열 때 보일 카메라는 조각 앞의 `*` 로 고른다(eric 2026-10-20 「컬러를 탭의 앞쪽으로 두고..
 * 염색기는 IR 를 디폴트」 — `stain=RGB:color|*IR:ir_left`). `*` 가 없으면 첫째다.
 */
export function visionStreamOptions(
  moduleKey: string,
  config: VisionConfig = visionConfig(),
): VisionStreamOption[] {
  const raw = config.streams[moduleKey] ?? config.defaultStream
  if (raw === undefined) return []
  const out: VisionStreamOption[] = []
  for (const piece of raw.split('|')) {
    const marked = piece.trim().startsWith('*')
    const part = marked ? piece.trim().slice(1) : piece
    const cut = part.indexOf(':')
    const stream = text(cut < 0 ? part : part.slice(cut + 1))
    if (stream === undefined) continue
    const label = text(cut < 0 ? undefined : part.slice(0, cut)) ?? stream
    out.push(marked ? { label, stream, primary: true } : { label, stream })
  }
  return out
}

/** 처음 열 때 보일 카메라 — `*` 로 고른 것, 없으면 첫째. 목록이 비면 `undefined` */
export function primaryVisionStream(options: readonly VisionStreamOption[]): string | undefined {
  return (options.find((option) => option.primary === true) ?? options[0])?.stream
}

/**
 * 이 모듈의 영상 서버 기준 경로. 모듈별 지정이 있으면 그것, 없으면 공용 `baseUrl` 이다.
 * 🔴 다른 모듈의 지정으로 떨어지지 않는다 — 지정 없는 모듈은 늘 공용으로 간다.
 */
export function visionBaseUrlFor(moduleKey: string, config: VisionConfig = visionConfig()): string {
  return config.baseUrls[moduleKey] ?? config.baseUrl
}

/**
 * 구독용 WebSocket 주소. 상대 경로면 현재 오리진 기준으로 `ws:` · `wss:` 를 만든다
 * (`config.ts` 의 `streamUrl` 과 같은 규칙이다). 기준 경로는 모듈이 고른다(`visionBaseUrlFor`).
 */
export function visionSocketUrl(
  stream: string,
  moduleKey: string,
  config: VisionConfig = visionConfig(),
): string {
  const base = visionBaseUrlFor(moduleKey, config)
  const query = `/api/ws?src=${encodeURIComponent(stream)}`
  if (/^https?:\/\//i.test(base)) {
    return base.replace(/^http/i, 'ws') + query
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}${base}${query}`
}

function text(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

function trimSlash(value: string): string {
  return value.replace(/\/+$/, '')
}

/** `cent=a,asm=b` → `{cent:'a', asm:'b'}`. 형이 틀린 조각은 조용히 버린다 */
function parseStreamMap(raw: string | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  for (const pair of (raw ?? '').split(',')) {
    const [key, value] = pair.split('=')
    const k = text(key)
    const v = text(value)
    if (k !== undefined && v !== undefined) out[k] = v
  }
  return out
}

/** 모듈별 기준 경로 — 키 형이 틀린 조각은 버리고 끝 `/` 를 뗀다 */
function baseUrlRecord(map: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(map)) {
    if (MODULE_KEY_RE.test(key)) out[key] = trimSlash(value)
  }
  return out
}

function streamRecord(value: unknown): Record<string, string> {
  if (!isRecord(value)) return {}
  const out: Record<string, string> = {}
  for (const [key, raw] of Object.entries(value)) {
    const v = text(raw)
    if (v !== undefined) out[key] = v
  }
  return out
}
